import { useEffect, useState } from "react";
import QRCode from "qrcode";
import api from "../api/client";
import { useAuth } from "../context/AuthContext";

function StatCard({ icon, label, value, sub, color }) {
  return (
    <div className={`bg-white rounded-xl shadow-sm border-l-4 ${color} p-5`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 font-medium">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
        </div>
        <span className="text-3xl">{icon}</span>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { usuario } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/dashboard/stats")
      .then((r) => setStats(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Usar subdominio del stats (más actualizado) o del contexto de auth como respaldo
  const subdominio = stats?.subdominio || usuario?.subdominio;

  useEffect(() => {
    if (!subdominio) return;
    const url = `${window.location.origin}/catalogo/${subdominio}`;
    QRCode.toDataURL(url, {
      width: 300,
      margin: 2,
      color: { dark: "#1e1b4b", light: "#ffffff" },
    }).then(setQrDataUrl);
  }, [subdominio]);
  const catalogoUrl = subdominio
    ? `${window.location.origin}/catalogo/${subdominio}`
    : null;

  const copiar = () => {
    if (!catalogoUrl) return;
    navigator.clipboard.writeText(catalogoUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const descargarQR = () => {
    if (!qrDataUrl) return;
    const a = document.createElement("a");
    a.href = qrDataUrl;
    a.download = `catalogo-${subdominio}-qr.png`;
    a.click();
  };

  if (loading)
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
      </div>
    );

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Dashboard</h2>
        {stats?.nombre_comercio && (
          <p className="text-gray-500 text-sm mt-1">{stats.nombre_comercio}</p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <StatCard icon="👁️" label="Visitas al menú" value={stats?.visitas_menu ?? 0} color="border-blue-500" />
        <StatCard icon="📦" label="Pedidos hoy" value={stats?.pedidos_hoy ?? 0} color="border-orange-500" />
        <StatCard icon="💵" label="Ventas hoy" value={`$${(stats?.ventas_hoy_usd ?? 0).toFixed(2)}`} color="border-green-500" />
        <StatCard icon="📈" label="Ventas del mes" value={`$${(stats?.ventas_mes_usd ?? 0).toFixed(2)}`} color="border-purple-500" />
        <StatCard icon="🛍️" label="Total productos" value={stats?.total_productos ?? 0} color="border-indigo-500" />
        <StatCard icon="🏷️" label="Categorías" value={stats?.total_categorias ?? 0} color="border-pink-500" />
      </div>

      {/* Enlace + QR */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Enlace del catálogo */}
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="font-semibold text-gray-700 mb-3">Tu enlace de catálogo</h3>
          {catalogoUrl ? (
            <>
              <div className="flex items-center gap-2 bg-indigo-50 rounded-lg px-3 py-2.5 mb-3">
                <span className="text-indigo-600 text-sm font-mono flex-1 truncate">{catalogoUrl}</span>
                <button
                  onClick={copiar}
                  className={`text-xs px-3 py-1.5 rounded-md font-medium transition-colors flex-shrink-0 ${
                    copied ? "bg-green-500 text-white" : "bg-indigo-600 text-white hover:bg-indigo-700"
                  }`}
                >
                  {copied ? "¡Copiado!" : "Copiar"}
                </button>
              </div>
              <a
                href={catalogoUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-xs text-indigo-600 hover:text-indigo-800 font-medium"
              >
                🔗 Abrir catálogo en nueva pestaña
              </a>
              <p className="text-xs text-gray-400 mt-2">
                Comparte este enlace con tus clientes
              </p>
            </>
          ) : (
            <p className="text-sm text-gray-400 italic">Cargando enlace...</p>
          )}
        </div>

        {/* Código QR */}
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="font-semibold text-gray-700 mb-3">Código QR del catálogo</h3>
          {qrDataUrl ? (
            <div className="flex items-center gap-5">
              <img
                src={qrDataUrl}
                alt="QR del catálogo"
                className="w-24 h-24 rounded-lg border border-gray-200"
              />
              <div>
                <p className="text-sm text-gray-600 mb-3">
                  Tus clientes pueden escanearlo con la cámara del celular para abrir tu catálogo.
                </p>
                <button
                  onClick={descargarQR}
                  className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  ⬇️ Descargar QR
                </button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic">
              {subdominio ? "Generando QR..." : "Sin URL configurada"}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
